import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiTool, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import toolService from '../services/toolService';
import Button from '../components/Button';
import './Technicians.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Tools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterStatus, effectiveLimit]);

  useEffect(() => {
    fetchTools();
  }, [currentPage, appliedSearch, filterStatus, effectiveLimit]);

  const fetchTools = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (filterStatus !== 'all') params.status = filterStatus;
      const response = await toolService.getAll(params);
      setTools(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch tools');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tool?')) {
      try {
        await toolService.delete(id);
        toast.success('Tool deleted successfully');
        fetchTools();
      } catch (error) {
        toast.error('Failed to delete tool');
      }
    }
  };

  const handleReturn = async (id) => {
    if (window.confirm('Mark this tool as returned?')) {
      try {
        await toolService.return(id);
        toast.success('Tool returned successfully');
        fetchTools();
      } catch (error) {
        toast.error('Failed to return tool');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      available: 'badge-success',
      in_use: 'badge-primary',
      under_maintenance: 'badge-warning',
      lost: 'badge-error',
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Tools & Equipment</h2>
          <p className="page-subtitle">Manage your shop tools</p>
        </div>
        <Link to="/tools/new">
          <Button variant="primary" icon={<FiPlus />}>Add Tool</Button>
        </Link>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="in_use">In Use</option>
          <option value="under_maintenance">Under Maintenance</option>
          <option value="lost">Lost</option>
        </select>
      </div>

      {tools.length === 0 ? (
        <div className="empty-state">
          <FiTool className="empty-icon" />
          <h3>No Tools Found</h3>
          <p>Get started by adding your first tool</p>
          <Link to="/tools/new">
            <Button variant="primary" icon={<FiPlus />}>Add Tool</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Code</th>
                  <th>Category</th>
                  <th>Condition</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((tool) => (
                  <tr key={tool._id}>
                    <td><strong>{tool.toolName}</strong></td>
                    <td>{tool.toolCode}</td>
                    <td>{tool.category}</td>
                    <td>{tool.condition?.replace('_', ' ') ?? '—'}</td>
                    <td>{tool.assignedTo?.name ?? '—'}</td>
                    <td>
                      <span className={getStatusBadge(tool.status)}>{tool.status.replace('_', ' ')}</span>
                    </td>
                    <td>
                      <div className="cell-actions">
                        {tool.status === 'in_use' && (
                          <button type="button" className="action-btn action-btn-view" onClick={() => handleReturn(tool._id)}>
                            Return
                          </button>
                        )}
                        <Link to={`/tools/${tool._id}/edit`} className="action-btn action-btn-edit"><FiEdit /> Edit</Link>
                        <button type="button" className="action-btn action-btn-delete" onClick={() => handleDelete(tool._id)}><FiTrash2 /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > tools.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select value={limitOption} onChange={(e) => setLimitOption(e.target.value)} className="pagination-limit-select">
                  {LIMIT_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input type="number" min={1} max={100} value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)} className="pagination-limit-input" />
                )}
              </div>
              <div className="pagination-controls">
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>Page {currentPage} of {pages}</span>
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))} disabled={currentPage === pages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Tools;
